import React, { type FunctionComponent, useState, useCallback, type FocusEvent } from 'react'
import { SVGIcon, type RadiumStyles } from 'ic-snacks'
import styled from '@emotion/styled'
import { type SVGIconProps } from 'ic-snacks/dist/esm/components/SVGIcon/SVGIcon'
import { dashColors } from 'legacy/dash-blocks/foundation/colors'
import { type OnChange, type OnBlur, type OnFocus } from 'utils/react/event.types'
import { type FlexProps, Flex } from 'legacy/dash-blocks/flex/Flex'
import { dashFontSizes } from 'legacy/dash-blocks/foundation/typography'
import { dashPixels } from 'legacy/dash-blocks/foundation/spacing-units/spacingUnits.types'
import { useOnEnterHandler } from 'legacy/common/utils/keyboard/keyboard.utils'
import { type OverridableStyles } from 'legacy/common/utils/styling/styling.types'
import { type FormFieldStateProps } from 'legacy/dash-components/forms/form-field/utils/FormField.types'

export interface DashTextFieldProps extends FlexProps, OverridableStyles, FormFieldStateProps {
  name: string
  value: string
  id?: string
  disabled?: boolean
  placeholder?: string
  type?: 'text' | 'number' | 'currency'
  readOnly?: boolean
  onEnter?: Function
  onChange?: OnChange
  onBlur?: OnBlur
  onFocus?: OnFocus
  prefixIcon?: SVGIconProps['name']
}

interface InputWrapperProps extends FormFieldStateProps {
  isFocused: boolean
}

const InputWrapper = styled(Flex)<InputWrapperProps>`
  background-color: ${dashColors.WHITE};
  border: 1px solid
    ${({ isFocused, hasError }) =>
      hasError ? dashColors.DANGER : isFocused ? dashColors.PRIMARY : dashColors.NEUTRAL};
  font-size: ${dashFontSizes.MEDIUM};
  height: ${dashPixels.PX36};
  border-radius: ${dashPixels.PX4};
  padding-left: 6px;
`

const StyledInput = styled.input`
  color: ${dashColors.DARKEST_NEUTRAL};
  outline: none;
  border: none;
  padding-left: ${dashPixels.PX4};
  font-size: ${dashFontSizes.SMALL};
  height: ${dashPixels.PX32};
  border-radius: ${dashPixels.PX4};
  width: 100%;
`

const svgStyles: RadiumStyles = { height: '18px' }

export const DashTextField: FunctionComponent<DashTextFieldProps> = ({
  name,
  value,
  type = 'text',
  onEnter,
  onChange,
  prefixIcon,
  placeholder,
  readOnly,
  disabled,
  overrideStyle = {},
  id,
  onBlur,
  onFocus,
  testId,
  ...rest
}) => {
  const prefixContent = prefixIcon ? (
    <Flex justify="center" align="center" fillHeight>
      <SVGIcon name="search" color={dashColors.NEUTRAL} style={svgStyles} />
    </Flex>
  ) : null

  const onEnterHandler = useOnEnterHandler(onEnter)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const handleFocus = useCallback(
    (e: FocusEvent) => {
      onFocus && onFocus(e)
      setIsFocused(true)
    },
    [onFocus]
  )
  const handleBlur = useCallback(
    (e: FocusEvent) => {
      onBlur && onBlur(e)
      setIsFocused(false)
    },
    [onBlur]
  )

  return (
    <InputWrapper isFocused={isFocused} wrap="nowrap" align="center" {...rest}>
      {prefixContent}
      <StyledInput
        id={id}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={onChange}
        onKeyPress={onEnterHandler}
        type={type}
        disabled={disabled}
        readOnly={readOnly}
        name={name}
        value={value}
        placeholder={placeholder}
        style={overrideStyle}
        data-testid={testId}
      />
    </InputWrapper>
  )
}
